import { XMarkIcon } from '@heroicons/react/16/solid'
import type { ReactNode } from 'react'
import type { CanadaGapCheckResult } from '../lib/canadagap'
import type { CanadaGapEntryIssue } from '../lib/types'

function Issues({ result }: { result: Pick<CanadaGapCheckResult, 'missing' | 'violations'> }) {
  return (
    <>
      {result.violations.map((v, i) => (
        <p key={i} className="mt-1 text-sm text-red-700 dark:text-red-400">
          ⚠ {v.message}
        </p>
      ))}
      {result.missing.length > 0 && (
        <p className="mt-1 text-sm text-amber-700 dark:text-amber-400">Missing: {result.missing.join(', ')}</p>
      )}
    </>
  )
}

/**
 * Props for a dialog's CanadaGAP ComplianceBadge from the live check of the entry being filled in:
 * ✓ when nothing is missing, amber for gaps, red for violations. {} until the first result.
 */
export function checkBadgeProps(result: CanadaGapCheckResult | null) {
  if (!result) return {}
  return {
    entryCount: 1,
    incompleteCount: result.missing.length > 0 ? 1 : 0,
    violationCount: result.violations.length > 0 ? 1 : 0,
    issues: [...result.violations.map((v) => v.message), ...result.missing.map((m) => `Missing: ${m}`)],
    hideCounts: true,
  }
}

/**
 * Under one input in an Add/Edit dialog: shown when the check placed a missing column on it. `product`
 * is the H1 product's position in the check request (omit for other inputs); `children` replaces the
 * default text.
 */
export function InputWarning({
  result,
  input,
  product,
  children,
}: {
  result: CanadaGapCheckResult | null
  input: string
  product?: number
  children?: ReactNode
}) {
  if (!result?.inputs.some((g) => g.input === input && g.product === product)) return null
  return <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">{children ?? 'Needed for CanadaGAP'}</p>
}

/**
 * Inside an Add/Edit dialog, above Save: the CanadaGAP warnings that don't belong to one input
 * (violations, and columns filled by either of two inputs), plus a count of the input warnings.
 * Never blocks saving.
 */
export function EntryCheckPanel({ result, formCode }: { result: CanadaGapCheckResult | null; formCode: string }) {
  if (!result) return null
  const placed = new Set(result.inputs.map((g) => g.label))
  const rest = { ...result, missing: result.missing.filter((label) => !placed.has(label)) }
  const inputCount = result.inputs.length
  const inputLine =
    inputCount > 0 ? `${inputCount} input${inputCount > 1 ? 's' : ''} above ${inputCount > 1 ? 'are' : 'is'} needed for CanadaGAP ${formCode}.` : null

  if (rest.missing.length === 0 && rest.violations.length === 0) {
    if (!inputLine) return <p className="mt-6 text-sm text-emerald-700 dark:text-emerald-400">✓ Nothing missing for CanadaGAP {formCode}</p>
    return <p className="mt-6 text-sm text-amber-700 dark:text-amber-400">{inputLine} You can still save.</p>
  }
  return (
    <div className="mt-6 rounded-lg bg-amber-50 p-3 ring-1 ring-amber-600/20 dark:bg-amber-900/20 dark:ring-amber-500/20">
      <p className="text-sm font-medium text-amber-800 dark:text-amber-300">CanadaGAP {formCode}</p>
      <Issues result={rest} />
      {inputLine && <p className="mt-1 text-sm text-amber-700 dark:text-amber-400">{inputLine}</p>}
      <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-400">You can still save. The entry stays flagged until these are fixed.</p>
    </div>
  )
}

/**
 * On the log page after a save: what the saved entries still lack for CanadaGAP, from the reloaded
 * status. `issues` holds only the saved entries that have gaps; `savedCount` is how many were saved
 * (a Form M round saves one entry per trap).
 */
export function SavedEntryNotice({
  formCode,
  issues,
  savedCount = 1,
  onDismiss,
}: {
  formCode: string
  issues: CanadaGapEntryIssue[]
  savedCount?: number
  onDismiss: () => void
}) {
  const several = savedCount > 1
  const box = issues.length
    ? 'bg-amber-50 ring-amber-600/20 dark:bg-amber-900/20 dark:ring-amber-500/20'
    : 'bg-emerald-50 ring-emerald-600/20 dark:bg-emerald-900/20 dark:ring-emerald-500/20'
  return (
    <div role="status" className={`mt-6 flex items-start gap-3 rounded-lg p-3 ring-1 ${box}`}>
      <div className="min-w-0 flex-1">
        {issues.length ? (
          <>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              {several
                ? `Saved ${savedCount} entries. ${issues.length} still incomplete for CanadaGAP ${formCode}.`
                : `Saved. This entry is still incomplete for CanadaGAP ${formCode}.`}
            </p>
            {issues.map((issue) => (
              <div key={issue.entryId}>
                {several && <p className="mt-1 text-sm font-medium text-amber-800 dark:text-amber-300">{issue.label}</p>}
                <Issues result={issue} />
              </div>
            ))}
          </>
        ) : (
          <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300">
            {several ? `Saved ${savedCount} entries.` : 'Saved.'} Nothing missing for CanadaGAP {formCode}.
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="rounded p-0.5 text-zinc-500 hover:bg-zinc-950/5 dark:text-zinc-400 dark:hover:bg-white/10"
      >
        <XMarkIcon className="size-4" />
      </button>
    </div>
  )
}
